import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import FlipIcon from '@mui/icons-material/Flip';
import Delete from '@mui/icons-material/Delete';
import './ToolsModal.css';

const ToolsModal = ({
  show,
  onHide,
  onToolSelect,
  title = "Select Tool"
}) => {
  const { t } = useTranslation();
  const drawerRef = useRef(null);
  const dragStateRef = useRef({
    isDragging: false,
    startPointerX: 0,
    startPointerY: 0,
    startLeft: 24,
    startTop: 24,
  });
  const [drawerPosition, setDrawerPosition] = useState({ left: 24, top: 24 });

  const clampDrawerPosition = (left, top) => {
    const drawerEl = drawerRef.current;
    if (!drawerEl || typeof window === 'undefined') {
      return { left, top };
    }

    const rect = drawerEl.getBoundingClientRect();
    const maxLeft = Math.max(12, window.innerWidth - rect.width - 12);
    const maxTop = Math.max(12, window.innerHeight - rect.height - 12);

    return {
      left: Math.min(Math.max(12, left), maxLeft),
      top: Math.min(Math.max(12, top), maxTop),
    };
  };

  const startDrag = (clientX, clientY) => {
    dragStateRef.current = {
      isDragging: true,
      startPointerX: clientX,
      startPointerY: clientY,
      startLeft: drawerPosition.left,
      startTop: drawerPosition.top,
    };
  };

  const handleHeaderMouseDown = (event) => {
    if (event.target.closest('.tools-drawer-close')) {
      return;
    }
    event.preventDefault();
    startDrag(event.clientX, event.clientY);
  };

  const handleHeaderTouchStart = (event) => {
    if (event.target.closest('.tools-drawer-close')) {
      return;
    }
    const touch = event.touches?.[0];
    if (!touch) return;
    startDrag(touch.clientX, touch.clientY);
  };

  useEffect(() => {
    if (!show) {
      document.body.classList.remove('tools-drawer-open');
      return undefined;
    }

    document.body.classList.add('tools-drawer-open');

    return () => {
      document.body.classList.remove('tools-drawer-open');
    };
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;

    const handlePointerMove = (clientX, clientY) => {
      const dragState = dragStateRef.current;
      if (!dragState.isDragging) return;

      const nextLeft = dragState.startLeft + (clientX - dragState.startPointerX);
      const nextTop = dragState.startTop + (clientY - dragState.startPointerY);
      setDrawerPosition(clampDrawerPosition(nextLeft, nextTop));
    };

    const handleMouseMove = (event) => {
      handlePointerMove(event.clientX, event.clientY);
    };

    const handleTouchMove = (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;
      handlePointerMove(touch.clientX, touch.clientY);
    };

    const stopDrag = () => {
      dragStateRef.current.isDragging = false;
    };

    const handleResize = () => {
      setDrawerPosition((current) => clampDrawerPosition(current.left, current.top));
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopDrag);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', stopDrag);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopDrag);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', stopDrag);
      window.removeEventListener('resize', handleResize);
    };
  }, [show, drawerPosition.left, drawerPosition.top]);

  useEffect(() => {
    if (!show) return;
    setDrawerPosition((current) => clampDrawerPosition(current.left, current.top));
  }, [show]);

  if (!show) {
    return null;
  }

  const tools = [
    {
      id: 'perspective',
      name: t('toolsSelection-perspective', 'Perspective'),
      icon: '/icons/Icons/perspective.svg',
      description: 'Enable perspective mode',
      category: 'view'
    },
    {
      id: 'draw',
      name: t('toolsSelection-draw', 'Draw'),
      icon: '/icons/pen.png',
      description: 'Draw on canvas',
      category: 'draw'
    },
    {
      id: 'erase',
      name: t('toolsSelection-eraser', 'Erase'),
      icon: '/icons/eraser-solid.png',
      description: 'Erase drawings',
      category: 'draw'
    },
    {
      id: 'removeall',
      name: t('app-removeAll', 'Remove All'),
      icon: '/icons/RemoveAllButton.png',
      description: 'Remove all objects',
      category: 'edit'
    },
    {
      id: 'cut',
      name: t('toolsSelection-cut', 'Cut'),
      iconComponent: <Delete />,
      description: 'Cut selected item',
      category: 'edit'
    },
    {
      id: 'copy',
      name: t('toolsSelection-copy', 'Copy'),
      icon: '/icons/copy.png',
      description: 'Copy selected item',
      category: 'edit'
    },
    {
      id: 'paste',
      name: t('toolsSelection-paste', 'Paste'),
      icon: '/icons/paste.png',
      description: 'Paste copied item',
      category: 'edit'
    },
    {
      id: 'clone',
      name: t('toolsSelection-clone', 'Clone'),
      icon: '/icons/paste.png',
      description: 'Duplicate selected item',
      category: 'edit'
    },
    {
      id: 'flipX',
      name: t('toolsSelection-flipx', 'Flip Horizontal'),
      iconComponent: <FlipIcon />,
      description: 'Flip horizontally',
      category: 'transform'
    },
    {
      id: 'flipY',
      name: t('toolsSelection-flipy', 'Flip Vertical'),
      iconComponent: <FlipIcon style={{ transform: 'rotate(90deg)' }} />,
      description: 'Flip vertically',
      category: 'transform'
    }
  ];

  const categories = [
    { id: 'view', name: t('toolsSelection-categoryView', 'View'), icon: '👁️' },
    { id: 'draw', name: t('toolsSelection-categoryDraw', 'Draw & Erase'), icon: '✏️' },
    { id: 'edit', name: t('toolsSelection-categoryEdit', 'Edit'), icon: '✂️' },
    { id: 'transform', name: t('toolsSelection-categoryTransform', 'Transform'), icon: '🔄' }
  ];

  const handleToolClick = (tool) => {
    if (onToolSelect) {
      onToolSelect(tool.id);
    }
  };

  const portalContainer = document.body;

  if (!portalContainer) {
    return null;
  }

  const drawerContent = (
    <>
      {/* Backdrop overlay */}
      {show && <div className="tools-drawer-backdrop" onClick={onHide} />}
      
      {/* Side Drawer */}
      <div
        ref={drawerRef}
        className={`tools-drawer ${show ? 'open' : ''}`}
        style={{ left: `${drawerPosition.left}px`, top: `${drawerPosition.top}px` }}
      >
        <div
          className="tools-drawer-header"
          onMouseDown={handleHeaderMouseDown}
          onTouchStart={handleHeaderTouchStart}
        >
          <h5 className="tools-drawer-title">{title || t('toolsSelection-tools', 'Tools')}</h5>
          <button className="tools-drawer-close" onClick={onHide} aria-label="Close">
            ×
          </button>
        </div>

        <div className="tools-drawer-body">
        {categories.map((category) => {
          const categoryTools = tools.filter(t => t.category === category.id);
          if (categoryTools.length === 0) return null;

          return (
            <div key={category.id} className="tool-category mb-4">
              <h6 className="category-title">
                <span className="category-icon">{category.icon}</span>
                {category.name}
              </h6>
              <div className="tools-grid">
                {categoryTools.map((tool) => (
                  <div
                    key={tool.id}
                    className="tool-item"
                    onClick={() => handleToolClick(tool)}
                    title={tool.description}
                  >
                    <div className="tool-icon">
                      {tool.iconComponent ? (
                        tool.iconComponent
                      ) : (
                        <img src={tool.icon} alt={tool.name} height="32" />
                      )}
                    </div>
                    <div className="tool-name">{tool.name}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        </div>
      </div>
    </>
  );

  // Use React Portal to render drawer in canvasContainer
  return ReactDOM.createPortal(drawerContent, portalContainer);
};

export default ToolsModal;
